import { getIn, setIn } from 'final-form';
import { indexBy, prop } from 'ramda';

import * as types from 'constants/ActionTypes';
import { SUCCESS_REQUEST } from 'modules/app/constants';
import * as appointmentTypes from 'modules/appointments/actionTypes';
import * as auditModelsTypes from 'modules/auditModels/actionTypes';
import * as auditTypes from 'modules/audits/actionTypes';
import { DETAILS_SOURCE } from 'modules/audits/constants';
import * as actionTypes from 'modules/dealers/actionTypes';
import { FETCH_DEVICES_SUCCESS, FETCH_DEVICE_SUCCESS } from 'modules/devices/actionTypes';
import { FETCH_INTERVENTION_SUCCESS } from 'modules/interventions/actionTypes';
import { FETCH_VAULT_SUCCESS } from 'modules/lockers/actionTypes';
import { SOCKET_CHECK } from 'modules/sockets/constants';
import * as stepTypes from 'modules/steps/actionTypes';
import { indexByProp } from 'utils/global';
import { createReducer } from 'utils/ReduxUtils';

const cleanDealer = dealer => {
  const haveOnlyPack = Object.keys(dealer.config ?? {}).length === 1 && 'pack' in dealer.config;
  const pack = dealer.config?.pack;

  if (haveOnlyPack) {
    const { config: _, ...formattedDealer } = dealer;
    return { ...formattedDealer, pack };
  }

  return {
    ...dealer,
    pack,
  };
};

/** @typedef {{[dealerId:string] : import('modules/dealers/_types/types').DEALER}} State */
const initialState = null;

// Fetch dealer
const fetchDealerSuccessReducer = (state, { payload: dealer }) => ({
  ...state,
  [dealer.id]: {
    ...(state?.[dealer.id] ?? {}),
    ...cleanDealer(dealer),
  },
});

/**
 * @param {State} state
 * @param {import('modules/dealers/types').GenericDealersActionPage} action
 * @returns {State}
 */
const fetchDealersSuccessReducer = (state, { payload }) => ({
  ...state,
  ...indexByProp(
    'id',
    payload.content.map(dealer => ({
      ...(state?.[dealer.id] ?? {}),
      ...cleanDealer(dealer),
    }))
  ),
});

const fetchDealersFailureReducer = () => initialState;

const dealerSuccessReducer = (state, { payload: dealer }) => ({
  ...state,
  [dealer.id]: cleanDealer(dealer),
});

const dealerByInterventionSuccessReducer = (state, { payload: intervention }) => {
  if (intervention.dealer) {
    return fetchDealerSuccessReducer(state, { payload: intervention.dealer });
  }
  return state;
};

const updateDealerFromItem = (state, { payload, meta = {} }) => {
  const shouldUpdate = 'dealer' in payload && (meta.source === DETAILS_SOURCE || !state?.[payload.dealer?.id]);
  return shouldUpdate ? fetchDealerSuccessReducer(state, { payload: payload.dealer }) : state;
};

/**
 * @param {State} state
 * @param {import('modules/dealerConfiguration/administration/types').SaveFollowUpConfigSuccessAction} action
 * @returns {State}
 */
const updateDealerFollowUpConfig = (state, { payload }) =>
  dealerSuccessReducer(state, {
    payload: {
      ...state[payload.dealerId],
      config: {
        ...state[payload.dealerId].config,
        followUp: payload.followUpConfig,
      },
    },
  });

const updateDealersFromCollection = (state, { payload }) => {
  const dealers = payload.content.map(({ dealer }) => dealer);

  return {
    ...state,
    ...indexBy(prop('id'))(
      dealers.filter(dealer => !!dealer).map(dealer => ({ ...state?.[dealer.id], ...cleanDealer(dealer) }))
    ),
  };
};

const updateDealers = (state, { payload }) => ({
  ...state,
  ...indexByProp(
    'id',
    payload.content.map(dealer => ({
      ...(state?.[dealer.id] ?? {}),
      ...cleanDealer(dealer),
    }))
  ),
});

const afterFetchDevices = (state, { payload: { content } }) => {
  const dealers = content.flatMap(device => [device.dealer, ...(device.userDealers ?? [])]).filter(Boolean);
  return updateDealers(state, { payload: { content: dealers } });
};

const hasPublicDealersMatcher = ({ type, payload, meta }) =>
  meta?.source !== SOCKET_CHECK && type?.endsWith(SUCCESS_REQUEST) && Array.isArray(payload?.dealers);

const updateWithPublicDealers = (state, { payload: { dealers } }) =>
  updateDealers(state, { payload: { content: dealers?.map(({ projectionType, ...dealer }) => dealer) } });

const updateStepAvailability = (state, { payload: { dealerId, mode, enabled, steps } }) => ({
  ...state,
  [dealerId]: setIn(state[dealerId], `config.steps.${mode}`, {
    ...getIn(state[dealerId], `config.steps.${mode}`),
    enabled,
    steps,
  }),
});

const updateStep = (state, { payload: { dealerId, mode, step } }) => {
  const existingSteps = getIn(state[dealerId], `config.steps.${mode}.steps`) || [];

  const index = existingSteps.findIndex(({ id: stepId }) => stepId === step.id);
  const newSteps = index >= 0 ? existingSteps.toSpliced(index, 1, step) : [...existingSteps, step];

  return {
    ...state,
    [dealerId]: setIn(state[dealerId], `config.steps.${mode}.steps`, newSteps),
  };
};

export default createReducer(
  initialState,
  {
    [actionTypes.GET_PAGINATED_DEALERS_FETCH_SUCCESS]: fetchDealersSuccessReducer,
    [types.FETCH_DEALER_SUCCESS]: fetchDealerSuccessReducer,
    [types.FETCH_DEALERS_SUCCESS]: fetchDealersSuccessReducer,
    [types.FETCH_DEALERS_FAILURE]: fetchDealersFailureReducer,
    [types.FETCH_DEALERS_SELECT_SUCCESS]: fetchDealersSuccessReducer,
    [types.FETCH_DEALERS_SELECT_FAILURE]: fetchDealersFailureReducer,
    [types.UPDATE_DEALER_SUCCESS]: dealerSuccessReducer,
    [types.CHANGE_DEALER_STATUS_SUCCESS]: dealerSuccessReducer,
    [types.CREATE_DEALER_SUCCESS]: dealerSuccessReducer,
    [FETCH_INTERVENTION_SUCCESS]: dealerByInterventionSuccessReducer,
    [FETCH_VAULT_SUCCESS]: updateDealerFromItem,
    [auditTypes.FETCH_AUDIT_SUCCESS]: updateDealerFromItem,
    [appointmentTypes.FETCH_APPOINTMENTS_SUCCESS]: updateDealersFromCollection,
    [appointmentTypes.FETCH_APPOINTMENT_SUCCESS]: updateDealerFromItem,
    [actionTypes.GET_MODAL_DEALERS_SUCCESS]: updateDealers,
    [auditModelsTypes.FETCH_AUDIT_MODELS_MY_SUCCESS]: updateDealersFromCollection,
    // Devices cases
    [FETCH_DEVICES_SUCCESS]: afterFetchDevices,
    [FETCH_DEVICE_SUCCESS]: updateDealerFromItem,
    // Follow  up configuration
    [stepTypes.SAVE_FOLLOW_UP_CONFIG_SUCCESS]: updateDealerFollowUpConfig,
    // Steps cases
    [stepTypes.PATCH_STEPS_MODE_SUCCESS]: updateStepAvailability,
    [stepTypes.UPDATE_STEP_SUCCESS]: updateStep,
    [stepTypes.CREATE_STEP_SUCCESS]: updateStep,
  },
  // Matchers
  [
    {
      matcher: hasPublicDealersMatcher,
      reducer: updateWithPublicDealers,
    },
  ]
);
